import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GetPickupsSummary, GetNewPickups, ReroutePickups } from '../../../services/pickupsService';
import { PickupSummary } from '../../../models/PickupSummary';
import { PickupNewOnCall } from '../../../models/PickupNewOnCall';
import { PickupStop } from '../../../models/ReroutePickupsRequest';
import { Link } from 'react-router-dom';

import NewOncall from './NewOncall';
import TerritoryBreak from './TerritoryBreak';
import SelectBox, { SelectBoxOption } from '../../Common/SelectBox';



function PickupSummaryView() {
  const [pickupsGroupedByTerritory, setPickupsGroupedByTerritory] = useState<{ [k: string]: PickupSummary[] }>({});
  const [newPickups, setNewPickups] = useState<PickupNewOnCall[]>([]);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const [routesByTerritory, setRoutesByTerritory] = useState<{ [k: string]: string[] }>({});
  const [selectedTerritory, setSelectedTerritory] = useState<string | number>('All');


  const fetchNewPickups = async () => {
    setNewPickups(await GetNewPickups());
  };

  const fetchPickupSummary = async () => {
    const pickups = await GetPickupsSummary();

    if (Array.isArray(pickups)) {
      const segregatedPickups = pickups.reduce<{ [k: string]: PickupSummary[] }>((acc, el) => {
        const { territoryName } = el;
        if (!acc[territoryName]) acc[territoryName] = [];
        acc[territoryName].push(el);

        return acc;
      }, {});

      setPickupsGroupedByTerritory(segregatedPickups);

      setLastRefresh(Date.now());

      const routes = pickups.reduce<{ [k: string]: string[] }>((acc, el) => {
        const { territoryName } = el;

        if (!acc[territoryName]) acc[territoryName] = [];
        acc[territoryName].push(el.puRoute);

        return acc;
      }, {});

      setRoutesByTerritory(routes);
    }
  };

  const refreshData = useCallback(() => {
    fetchPickupSummary();
    fetchNewPickups();
  }, []);

  const autoRefreshInterval = 60 * 1000;
  const interval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  useEffect(() => {
    refreshData();

    interval.current = setInterval(() => {
      fetchPickupSummary();
      fetchNewPickups();
    }, autoRefreshInterval);

    return () => {
      clearInterval(interval.current);
    };
  }, [refreshData, autoRefreshInterval]);

  const toggleAutoRefresh = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    if (checked) {
      if (interval.current) clearInterval(interval.current);

      interval.current = setInterval(() => {
        fetchPickupSummary();
        fetchNewPickups();
      }, autoRefreshInterval);
    } else {
      if (interval.current) clearInterval(interval.current);
      interval.current = undefined;
    }
  };

  const routePickup = (toRoute: string, stops: PickupStop[]) => {
    ReroutePickups(undefined, toRoute, stops).then(() => {
      refreshData();
    });
  };

  const territoryOptions: SelectBoxOption[] = Object.keys(pickupsGroupedByTerritory).map((territory) => {
    return { label: territory, value: territory };
  });

  territoryOptions.unshift({ label: 'All', value: 'All' });

  return (
    <div className="xs:mx-1 max-w-[800px] sm:mx-10 md:mx-auto">
      <div className="ml-3 flex flex-row items-end justify-between">
        <div className="flex flex-col">
          <h1 className="mt-5 text-3xl font-bold text-blue-800">Pickups</h1>
          <div className="mb-1 text-sm font-normal italic text-gray-800">
            (last refresh {new Date(lastRefresh).toLocaleTimeString()})
          </div>
        </div>
        <div className="flex flex-row space-x-2">
          <label htmlFor="autorefresh">Auto Refresh</label>
          <input
            type="checkbox"
            id="autorefresh"
            className={` h-5 w-5 rounded text-blue-600 focus:ring-2`}
            onChange={toggleAutoRefresh}
            defaultChecked
          />
        </div>
      </div>
      <div className="my-5 flex flex-row items-end justify-between">
        <div className="flex flex-col items-center">
          <div className="mr-2 font-semibold text-gray-700">Territory</div>
          <SelectBox options={territoryOptions} selected={selectedTerritory} setSelected={setSelectedTerritory} />
        </div>
        <div className="flex flex-col items-baseline space-x-5">
          <button onClick={refreshData} className="btn-primary ml-3">
            Refresh
          </button>
        </div>
      </div>

      {newPickups.length > 0 && (
        <NewOncall
          pickups={newPickups}
          routes={routesByTerritory}
          selectedTerritory={selectedTerritory}
          routePickup={routePickup}
        />
      )}


      <div className="grid grid-cols-4 space-x-1 bg-slate-600 py-1 font-mono uppercase text-white  drop-shadow-md sm:text-lg md:text-2xl">
        <div className="pl-2">Route</div>
        <div className="pl-2">Scheduled</div>
        <div className="pl-2">On-Call</div>
        <div className="pl-2">Dropbox</div>
      </div>

      {Object.entries(pickupsGroupedByTerritory).map(([territoryName, pickupSummary]) => {
        if (selectedTerritory !== 'All' && selectedTerritory !== territoryName) return <div key={territoryName}></div>;

        return (
          <div key={territoryName}>
            {selectedTerritory === 'All' && <TerritoryBreak territory={territoryName} />}
            {pickupSummary.map((pickup, idx) => {
              return (
                <div
                  className={`grid grid-cols-4 border-b border-b-slate-300 py-1 text-xl font-semibold hover:cursor-pointer hover:bg-blue-200 ${
                    idx % 2 === 0 ? 'bg-slate-200' : ''
                  }`}
                  key={pickup.puRoute}>
                  <div className={`pl-4 font-bold uppercase text-blue-800 `}>
                    <Link to={pickup.puRoute.toUpperCase()}>{pickup.puRoute}</Link>
                  </div>
                  <div className="pl-5">
                    {pickup.totalSchdStops}
                  </div>
                  <div className="pl-5">
                    {pickup.totalOncallStops}
                  </div>
                  <div className="pl-5">
                    {pickup.totalDropBoxStops}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default PickupSummaryView;
